import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { T, sans } from '../Vendors/VendorDetails';

/**
 * The Settings page's design kit. Every Settings section (including AI providers, financial data and API keys)
 * is built from these pieces so the whole page reads as one product: same palette, cards, rows, buttons,
 * inputs, dialogs and save bar, in light and dark. Inline-token style, no MUI -- the app's newer convention.
 */

export type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'muted';

/** Every Settings section receives these from the shell. A section renders its own SectionHeader. */
export interface SettingsSectionProps {
  isDark: boolean;
  isAdmin: boolean;
  notify: (text: string, tone?: 'ok' | 'error') => void;
  /** Tells the shell this section has unsaved edits, so leaving it asks first. */
  setDirty?: (dirty: boolean) => void;
}

export function palette(isDark: boolean) {
  return {
    isDark,
    ink: isDark ? '#e5e7eb' : T.ink,
    slate: isDark ? '#94a3b8' : T.slate,
    muted: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr,
    softLine: isDark ? '#171717' : '#EEF2F6',
    surface: isDark ? '#0d0d0d' : T.surface,
    surface2: isDark ? '#141414' : T.surface2,
    hover: isDark ? '#151515' : '#F1F5F9',
    bg: isDark ? '#000' : T.bg,
    accent: isDark ? '#3B82F6' : T.blue,
    accentBg: isDark ? '#0a1628' : T.blueBg,
    accentBdr: isDark ? '#1e3a8a' : T.blueBdr,
    danger: isDark ? '#F87171' : T.red,
    dangerBg: isDark ? '#2a1414' : T.redBg,
    dangerBdr: isDark ? '#4c1d1d' : T.redBdr,
    amber: isDark ? '#FBBF24' : T.amb,
    amberBg: isDark ? '#2a2114' : T.ambBg,
    amberBdr: isDark ? '#3a2e10' : T.ambBdr,
    green: isDark ? '#4ADE80' : T.grn,
    greenBg: isDark ? '#132a1b' : T.grnBg,
    greenBdr: isDark ? '#1d3b27' : T.grnBdr,
    chip: isDark ? '#1a1a1a' : '#F1F5F9',
  };
}
export type Palette = ReturnType<typeof palette>;

export const toneColors = (c: Palette, tone: Tone) => ({
  ok: { fg: c.green, bg: c.greenBg, bdr: c.greenBdr },
  warn: { fg: c.amber, bg: c.amberBg, bdr: c.amberBdr },
  danger: { fg: c.danger, bg: c.dangerBg, bdr: c.dangerBdr },
  info: { fg: c.accent, bg: c.accentBg, bdr: c.accentBdr },
  muted: { fg: c.slate, bg: c.chip, bdr: c.line },
}[tone]);

export const apiError = (e: any, fallback: string) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg).replace(/^Value error, /, '');
  return fallback;
};
export const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
export const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
export function timeAgo(iso?: string | null) {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)} d ago`;
  return fmtDate(iso);
}

/* ─── Buttons & inputs ─────────────────────────────────────────────────────────────────── */

export type ButtonKind = 'primary' | 'secondary' | 'danger' | 'ghost';

export function btn(c: Palette, kind: ButtonKind = 'secondary', disabled = false): CSSProperties {
  const base: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 14px',
    borderRadius: 6, fontSize: 12.5, fontWeight: 600, fontFamily: sans, cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap', lineHeight: 1.3, transition: 'background .12s, border-color .12s',
  };
  if (kind === 'primary') return { ...base, border: 'none', background: c.isDark ? '#3B82F6' : T.ink, color: '#fff' };
  if (kind === 'danger') return { ...base, border: `1px solid ${c.dangerBdr}`, background: c.dangerBg, color: c.danger };
  if (kind === 'ghost') return { ...base, border: 'none', background: 'transparent', color: c.accent, padding: '7px 8px' };
  return { ...base, border: `1px solid ${c.line}`, background: c.surface, color: c.ink };
}

export function Button({ c, kind = 'secondary', disabled, busy, onClick, children, type = 'button', style, title }: {
  c: Palette; kind?: ButtonKind; disabled?: boolean; busy?: boolean; onClick?: () => void; children: ReactNode;
  type?: 'button' | 'submit'; style?: CSSProperties; title?: string;
}) {
  const off = disabled || busy;
  return (
    <button type={type} title={title} disabled={off} onClick={onClick} style={{ ...btn(c, kind, off), ...style }}>
      {busy && <Spinner c={c} light={kind === 'primary'} />}
      {children}
    </button>
  );
}

export function Spinner({ c, light, size = 12 }: { c: Palette; light?: boolean; size?: number }) {
  return (
    <span aria-hidden style={{
      width: size, height: size, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
      border: `2px solid ${light ? 'rgba(255,255,255,.35)' : c.line}`, borderTopColor: light ? '#fff' : c.accent,
      animation: 'settingsSpin .7s linear infinite',
    }} />
  );
}

export const inputStyle = (c: Palette, invalid = false): CSSProperties => ({
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: sans, color: c.ink,
  background: c.surface, border: `1px solid ${invalid ? c.danger : c.line}`, borderRadius: 6, outline: 'none',
});
export const selectStyle = (c: Palette): CSSProperties => ({ ...inputStyle(c), cursor: 'pointer', width: 'auto', minWidth: 160 });

export const fieldLabel = (c: Palette): CSSProperties => ({
  display: 'block', fontSize: 12, fontWeight: 600, color: c.ink, marginBottom: 6,
});

export function Field({ c, label, hint, error, children }: { c: Palette; label: string; hint?: ReactNode; error?: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={fieldLabel(c)}>{label}</span>
      {children}
      {error ? <span style={{ display: 'block', fontSize: 11.5, color: c.danger, marginTop: 5 }}>{error}</span>
        : hint ? <span style={{ display: 'block', fontSize: 11.5, color: c.muted, marginTop: 5, lineHeight: 1.45 }}>{hint}</span> : null}
    </label>
  );
}

export function Toggle({ c, checked, onChange, disabled, label }: {
  c: Palette; checked: boolean; onChange: (next: boolean) => void; disabled?: boolean; label: string;
}) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 36, height: 20, borderRadius: 99, border: 'none', padding: 2, flexShrink: 0, position: 'relative',
        background: checked ? c.accent : (c.isDark ? '#2a2a2a' : '#CBD5E1'), cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1, transition: 'background .15s',
      }}>
      <span style={{
        position: 'absolute', top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.25)',
      }} />
    </button>
  );
}

/** Segmented choice: a small set of mutually exclusive options shown side by side. */
export function Segmented<V extends string | number>({ c, value, options, onChange, disabled }: {
  c: Palette; value: V; options: { value: V; label: string }[]; onChange: (v: V) => void; disabled?: boolean;
}) {
  return (
    <div role="radiogroup" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, padding: 3, borderRadius: 8, background: c.chip }}>
      {options.map(o => {
        const on = o.value === value;
        return (
          <button key={String(o.value)} type="button" role="radio" aria-checked={on} disabled={disabled} onClick={() => onChange(o.value)}
            style={{
              padding: '5px 11px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: on ? 700 : 500, fontFamily: sans,
              cursor: disabled ? 'not-allowed' : 'pointer', color: on ? c.ink : c.slate,
              background: on ? c.surface : 'transparent', boxShadow: on ? (c.isDark ? '0 0 0 1px #2a2a2a' : '0 1px 2px rgba(15,23,42,.12)') : 'none',
            }}>{o.label}</button>
        );
      })}
    </div>
  );
}

/* ─── Layout ───────────────────────────────────────────────────────────────────────────── */

export function SectionHeader({ c, title, description, actions }: { c: Palette; title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
      <div style={{ minWidth: 0, maxWidth: 640 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: c.ink, margin: 0, letterSpacing: '-.01em' }}>{title}</h2>
        {description && <p style={{ fontSize: 13, color: c.slate, margin: '4px 0 0', lineHeight: 1.55 }}>{description}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

export function Panel({ c, title, description, actions, children, footer, flush, badge, id }: {
  c: Palette; title?: ReactNode; description?: ReactNode; actions?: ReactNode; children?: ReactNode; footer?: ReactNode;
  flush?: boolean; badge?: ReactNode; id?: string;
}) {
  return (
    <section id={id} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10, overflow: 'hidden', scrollMarginTop: 80 }}>
      {(title || actions) && (
        <div style={{
          padding: '16px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          borderBottom: children ? `1px solid ${c.line}` : 'none',
        }}>
          <div style={{ minWidth: 0, flex: '1 1 280px' }}>
            {title && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: c.ink }}>{title}</span>{badge}
              </div>
            )}
            {description && <div style={{ fontSize: 12.5, color: c.slate, marginTop: 3, lineHeight: 1.5, maxWidth: 620 }}>{description}</div>}
          </div>
          {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</div>}
        </div>
      )}
      {children !== undefined && children !== null && <div style={{ padding: flush ? 0 : '16px 20px' }}>{children}</div>}
      {footer && (
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, background: c.surface2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          {footer}
        </div>
      )}
    </section>
  );
}

/** Label + description on the left, the control on the right; stacks on narrow screens. */
export function SettingRow({ c, label, description, children, first, align = 'center' }: {
  c: Palette; label: ReactNode; description?: ReactNode; children?: ReactNode; first?: boolean; align?: 'center' | 'start';
}) {
  return (
    <div style={{
      display: 'flex', alignItems: align === 'center' ? 'center' : 'flex-start', justifyContent: 'space-between', gap: '10px 24px',
      flexWrap: 'wrap', padding: '14px 20px', borderTop: first ? 'none' : `1px solid ${c.softLine}`,
    }}>
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{label}</div>
        {description && <div style={{ fontSize: 12, color: c.slate, marginTop: 3, lineHeight: 1.5, maxWidth: 560 }}>{description}</div>}
      </div>
      {children && <div style={{ flex: '0 1 auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{children}</div>}
    </div>
  );
}

export function Badge({ c, tone = 'muted', children, dot }: { c: Palette; tone?: Tone; children: ReactNode; dot?: boolean }) {
  const t = toneColors(c, tone);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
      color: t.fg, background: t.bg, whiteSpace: 'nowrap', lineHeight: 1.5,
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.fg }} />}
      {children}
    </span>
  );
}

export function Notice({ c, tone = 'info', title, children, action }: { c: Palette; tone?: Tone; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const t = toneColors(c, tone);
  return (
    <div role={tone === 'danger' ? 'alert' : 'note'} style={{
      display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap',
      padding: '11px 14px', borderRadius: 8, background: t.bg, border: `1px solid ${t.bdr}`, fontSize: 12.5, lineHeight: 1.5,
    }}>
      <div style={{ minWidth: 0, flex: '1 1 260px', color: c.ink }}>
        {title && <div style={{ fontWeight: 700, color: t.fg, marginBottom: children ? 2 : 0 }}>{title}</div>}
        {children && <div style={{ color: c.slate }}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ c, title, body, action }: { c: Palette; title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ padding: '34px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: c.ink }}>{title}</div>
      {body && <div style={{ fontSize: 12.5, color: c.slate, marginTop: 6, maxWidth: 420, marginInline: 'auto', lineHeight: 1.55 }}>{body}</div>}
      {action && <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center', gap: 8 }}>{action}</div>}
    </div>
  );
}

export function LoadingBlock({ c, rows = 3 }: { c: Palette; rows?: number }) {
  return (
    <div aria-busy style={{ display: 'grid', gap: 12 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ height: i === 0 ? 92 : 64, borderRadius: 10, background: c.surface, border: `1px solid ${c.line}`, overflow: 'hidden', position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, transparent, ${c.surface2}, transparent)`, animation: 'settingsShimmer 1.3s infinite' }} />
        </div>
      ))}
    </div>
  );
}

export function ErrorBlock({ c, message, onRetry }: { c: Palette; message: string; onRetry?: () => void }) {
  return (
    <Panel c={c}>
      <EmptyState c={c} title="Couldn't load this section" body={message}
        action={onRetry && <Button c={c} onClick={onRetry}>Try again</Button>} />
    </Panel>
  );
}

/** Admin-only explanation shown in place of disabled controls. */
export function ReadOnlyHint({ c, children = 'Only admins can change these settings.' }: { c: Palette; children?: ReactNode }) {
  return <span style={{ fontSize: 11.5, color: c.muted }}>{children}</span>;
}

/* ─── Overlays ─────────────────────────────────────────────────────────────────────────── */

function useEscape(active: boolean, onEscape: () => void) {
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onEscape(); } };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [active, onEscape]);
}

export function Modal({ c, open, title, description, onClose, children, footer, width = 520, closeOnBackdrop = true }: {
  c: Palette; open: boolean; title: ReactNode; description?: ReactNode; onClose: () => void; children?: ReactNode;
  footer?: ReactNode; width?: number; closeOnBackdrop?: boolean;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div role="presentation" onMouseDown={e => { if (closeOnBackdrop && e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1450, background: c.isDark ? 'rgba(0,0,0,.7)' : 'rgba(15,23,42,.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 16px 16px', overflowY: 'auto', fontFamily: sans,
      }}>
      <div role="dialog" aria-modal="true" style={{
        width: '100%', maxWidth: width, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12,
        boxShadow: c.isDark ? '0 20px 60px rgba(0,0,0,.8)' : '0 20px 50px rgba(15,23,42,.22)', animation: 'settingsPop .14s ease-out',
      }}>
        <div style={{ padding: '18px 20px 0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.ink }}>{title}</div>
            {description && <div style={{ fontSize: 12.5, color: c.slate, marginTop: 4, lineHeight: 1.5 }}>{description}</div>}
          </div>
          <button aria-label="Close" onClick={onClose} style={{
            border: 'none', background: 'transparent', color: c.muted, fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 2, height: 24,
          }}>×</button>
        </div>
        <div style={{ padding: '16px 20px' }}>{children}</div>
        {footer && (
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({ c, open, title, body, confirmLabel = 'Confirm', tone = 'primary', busy, onConfirm, onCancel }: {
  c: Palette; open: boolean; title: string; body?: ReactNode; confirmLabel?: string; tone?: 'primary' | 'danger'; busy?: boolean;
  onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <Modal c={c} open={open} title={title} onClose={() => { if (!busy) onCancel(); }} width={440}
      footer={<>
        <Button c={c} onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button c={c} kind={tone === 'danger' ? 'danger' : 'primary'} busy={busy} onClick={onConfirm}>{confirmLabel}</Button>
      </>}>
      {body && <div style={{ fontSize: 13, color: c.slate, lineHeight: 1.55 }}>{body}</div>}
    </Modal>
  );
}

/** Sticky "unsaved changes" bar at the bottom of the viewport while a form differs from what's saved. */
export function SaveBar({ c, visible, saving, onDiscard, onSave, message = 'You have unsaved changes', saveLabel = 'Save changes', canSave = true }: {
  c: Palette; visible: boolean; saving?: boolean; onDiscard: () => void; onSave: () => void; message?: string; saveLabel?: string; canSave?: boolean;
}) {
  if (!visible) return null;
  return (
    <div role="region" aria-label="Unsaved changes" style={{
      position: 'sticky', bottom: 16, zIndex: 20, marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      flexWrap: 'wrap', padding: '10px 14px 10px 16px', borderRadius: 10, background: c.isDark ? '#111827' : T.ink, color: '#fff',
      boxShadow: c.isDark ? '0 10px 30px rgba(0,0,0,.7)' : '0 10px 30px rgba(15,23,42,.25)', animation: 'settingsRise .16s ease-out',
    }}>
      <span style={{ fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#FBBF24' }} />{message}
      </span>
      <span style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onDiscard} disabled={saving} style={{
          ...btn(c, 'secondary', !!saving), background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.25)',
        }}>Discard</button>
        <button type="button" onClick={onSave} disabled={saving || !canSave} style={{
          ...btn(c, 'primary', !!saving || !canSave), background: '#3B82F6', color: '#fff',
        }}>{saving && <Spinner c={c} light />}{saving ? 'Saving…' : saveLabel}</button>
      </span>
    </div>
  );
}

/** Read-only value with a copy button (workspace id, newly created API key, example requests). */
export function CopyField({ c, value, secret, multiline }: { c: Palette; value: string; secret?: boolean; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(!secret);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); window.setTimeout(() => setCopied(false), 1800); } catch { /* clipboard blocked */ }
  };
  return (
    <div style={{
      display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: 8, padding: '7px 8px 7px 12px', borderRadius: 7,
      background: c.surface2, border: `1px solid ${c.line}`, minWidth: 0,
    }}>
      <span style={{
        flex: 1, minWidth: 0, fontSize: 12.5, color: c.ink, fontFamily: sans, wordBreak: 'break-all',
        whiteSpace: multiline ? 'pre-wrap' : 'nowrap', overflow: multiline ? 'visible' : 'hidden', textOverflow: 'ellipsis', lineHeight: 1.55,
      }}>{shown ? value : '•'.repeat(Math.min(32, value.length))}</span>
      {secret && <button type="button" onClick={() => setShown(s => !s)} style={btn(c, 'ghost')}>{shown ? 'Hide' : 'Show'}</button>}
      <button type="button" onClick={copy} style={{ ...btn(c, 'secondary'), padding: '5px 10px', fontSize: 12 }}>{copied ? 'Copied ✓' : 'Copy'}</button>
    </div>
  );
}

/** Keyframes shared by the kit (spinner, skeleton shimmer, dialog pop, save bar rise). Render once per page. */
export function SettingsKeyframes() {
  return (
    <style>{`
      @keyframes settingsSpin { to { transform: rotate(360deg); } }
      @keyframes settingsShimmer { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
      @keyframes settingsPop { from { opacity: 0; transform: translateY(6px) scale(.985); } to { opacity: 1; transform: none; } }
      @keyframes settingsRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
      @keyframes settingsFade { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
    `}</style>
  );
}

/** Small table helpers in the kit's look. */
export const tableHead = (c: Palette): CSSProperties => ({
  padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: c.slate,
  background: c.surface2, borderBottom: `1px solid ${c.line}`, whiteSpace: 'nowrap',
});
export const tableCell = (c: Palette): CSSProperties => ({
  padding: '11px 14px', fontSize: 12.5, color: c.ink, borderBottom: `1px solid ${c.softLine}`, verticalAlign: 'middle',
});
